import { Component } from 'react'
import styled from '@emotion/styled'
import { Spinner } from '@chakra-ui/react'
import { Box, Button, Modal, Text } from 'stylewhere/components'
import { __ } from 'stylewhere/shared/i18n'

export type PreEncodingCorrectionPhase = 'reading' | 'epc_ready' | 'read_error' | 'writing' | 'write_error'

interface Props {
  visible: boolean
  instruction: string
  phase: PreEncodingCorrectionPhase
  epcLabel?: string
  onWrite: () => void
  onRetryRead: () => void
  onRetryWrite: () => void
  onCancel: () => void
}

const isStaticPhase = (phase: PreEncodingCorrectionPhase) =>
  phase === 'epc_ready' || phase === 'read_error' || phase === 'write_error'

export class PreEncodingCorrectionModal extends Component<Props> {
  renderEpcLine = () => {
    const { phase, epcLabel } = this.props
    if (phase === 'reading') {
      return (
        <EpcLine>
          <Text fontSize={20} bold>
            {__('pre_encoding.epc_reading')}
          </Text>
        </EpcLine>
      )
    }
    if (phase === 'read_error') {
      return (
        <EpcLine>
          <Text fontSize={18} center style={{ opacity: 0.7 }}>
            {__('pre_encoding.correction_read_error')}
          </Text>
        </EpcLine>
      )
    }
    if (phase === 'writing') {
      return (
        <EpcLine>
          {epcLabel && (
            <Text fontSize={20} bold style={{ marginBottom: 12 }}>
              {__('pre_encoding.epc_label', { epc: epcLabel })}
            </Text>
          )}
          <Text fontSize={20} bold>
            {__('pre_encoding.correction_in_progress')}
          </Text>
        </EpcLine>
      )
    }
    if (phase === 'epc_ready' && epcLabel) {
      return (
        <EpcLine>
          <Text fontSize={20} bold>
            {__('pre_encoding.epc_label', { epc: epcLabel })}
          </Text>
        </EpcLine>
      )
    }
    return null
  }

  renderAction = () => {
    const { phase, onWrite, onRetryRead, onRetryWrite } = this.props

    if (phase === 'read_error') {
      return <ActionButton title={__('misc.retry')} onClick={onRetryRead} />
    }

    if (phase === 'write_error') {
      return <ActionButton title={__('misc.retry')} onClick={onRetryWrite} />
    }

    if (phase === 'writing') {
      return (
        <WriteButton disabled>
          <Spinner size="lg" color="#000000" style={{ marginRight: 16 }} />
        </WriteButton>
      )
    }

    if (phase === 'epc_ready') {
      return <WriteButton title={__('pre_encoding.write_correction')} onClick={onWrite} />
    }

    if (phase === 'reading') {
      return (
        <WriteButton disabled>
          <Spinner size="lg" color="#000000" />
        </WriteButton>
      )
    }

    return null
  }

  renderCancel = () => {
    const { phase, onCancel } = this.props
    if (!isStaticPhase(phase)) return null

    return <CancelButton title={__('misc.cancel')} onClick={onCancel} style={{ marginTop: 16 }} />
  }

  render() {
    const { visible, instruction, phase } = this.props
    const showInstruction = phase === 'reading' || phase === 'read_error'
    return (
      <Modal visible={visible} onClose={() => undefined} size="xl" showCloseButton={false} closeOnOverlayClick={false}>
        <Box center pb={20}>
          <Text bold fontSize={28} style={{ marginBottom: 24 }}>
            {__('pre_encoding.correction_title')}
          </Text>
          {showInstruction && (
            <Text fontSize={20} center style={{ marginBottom: 32, opacity: 0.7, maxWidth: 560 }}>
              {instruction}
            </Text>
          )}
          {this.renderEpcLine()}
          <Box mt={40} center style={{ width: '100%' }}>
            {this.renderAction()}
            {this.renderCancel()}
          </Box>
        </Box>
      </Modal>
    )
  }
}

const EpcLine = styled(Box)`
  min-height: 32px;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`

const WriteButton = styled(Button)`
  background: #c4f542;
  color: #000000;
  border-radius: 100px;
  min-width: 320px;
  height: 72px;
  font-size: 22px;
  font-weight: 700;
`

const ActionButton = styled(Button)`
  background: #333333;
  color: #ffffff;
  border-radius: 100px;
  min-width: 240px;
  height: 60px;
  font-size: 20px;
`

const CancelButton = styled(Button)`
  background: transparent;
  color: #666666;
  border: 2px solid #cccccc;
  border-radius: 100px;
  min-width: 240px;
  height: 56px;
  font-size: 18px;
`
